<template>
  <div class="daily-free-content my-5">
    <div class="daily-free-header rounded-top-lg lh-1">
      <div class="row">
        <div class="col">時段</div>
        <div class="col text-center">平日</div>
        <div class="col text-center">週六</div>
        <div class="col text-center">例假日</div>
      </div>
    </div>
    <div class="row g-0 daily-free-form-row border-bottom border-bottom-dashed">
      <div class="col text-center pe-2 fw-bold">
        <div class="fs-5 lh-1">早班</div>
        <div class="small">5:00~11:00</div>
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.NorMorning" />
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.SatMorning" />
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.SunMorning" />
      </div>
    </div>
    <div class="row g-0 daily-free-form-row border-bottom border-bottom-dashed">
      <div class="col text-center pe-2 fw-bold">
        <div class="fs-5 lh-1">午班</div>
        <div class="small">12:00~17:00</div>
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.NorAfternoon" />
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.SatAfternoon" />
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.SunAfternoon" />
      </div>
    </div>
    <div class="row g-0 daily-free-form-row border-bottom border-bottom-dashed">
      <div class="col text-center pe-2 fw-bold">
        <div class="fs-5 lh-1">晚班</div>
        <div class="small">18:00~23:00</div>
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.NorNight" />
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.SatNight" />
      </div>
      <div class="col">
        <LargeCheckBox v-model="form.SunNight" />
      </div>
    </div>
    <div class="mt-3 py-3">
      <button
        class="btn btn-primary w-100 text-white text-center"
        @click="updateForm">
        <span class="fs-5 fw-bold text-spacing-5">送出</span>
      </button>
      <div class="small text-scrondary">
        最後更新時間：{{ LastUpdateTimeFormat(lastUpdateTime) }}
      </div>
    </div>
    <div
      v-if="pageState.saveSuccess === true"
      class="border border-5 border-success rounded p-2 text-center bg-success bg-opacity-75">
      <span class="fs-5 fw-bold">儲存成功</span>
    </div>
    <div
      v-if="pageState.saveSuccess === false"
      class="border border-5 border-danger rounded p-2 text-center bg-danger bg-opacity-75">
      <span class="fs-5 fw-bold">儲存失敗</span>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, reactive, ref } from "vue";
import LargeCheckBox from "@/components/large-checkbox.vue";
import { DailyFreeTimeUpdateModel } from "@/interface/reportform";
import {
  GetDailyFreeTime,
  UpsertDailyFreeTime,
} from "@/api/driverApi/driverapi";
import { isValid, parseISO } from "date-fns";
import { format } from "date-fns";

export default defineComponent({
  components: {
    LargeCheckBox,
  },
  async setup() {
    /** 表單內容 */
    const form = reactive<DailyFreeTimeUpdateModel>({
      NorMorning: false,
      NorAfternoon: false,
      NorNight: false,
      SatMorning: false,
      SatAfternoon: false,
      SatNight: false,
      SunMorning: false,
      SunAfternoon: false,
      SunNight: false,
    });
    /** 最後更新時間 */
    const lastUpdateTime = ref<Date | null>(null);
    /** 頁面狀態 */
    const pageState = reactive<PageState>({
      loading: false,
      loadSuccess: null,
      saving: false,
      saveSuccess: null,
    });

    /** 取資料 */
    const fetchData = async () => {
      pageState.loading = true;
      pageState.loadSuccess = null;

      try {
        const { data, response } = await GetDailyFreeTime();

        if (!response.ok) throw new Error("處理時發生錯誤，請稍後再試");

        if (data) {
          form.NorMorning = data.NorMorning;
          form.NorAfternoon = data.NorAfternoon;
          form.NorNight = data.NorNight;
          form.SatMorning = data.SatMorning;
          form.SatAfternoon = data.SatAfternoon;
          form.SatNight = data.SatNight;
          form.SunMorning = data.SunMorning;
          form.SunAfternoon = data.SunAfternoon;
          form.SunNight = data.SunNight;

          const date = parseISO(data.LastUpdateTime as unknown as string);
          lastUpdateTime.value = isValid(date) ? date : null;
        } else {
          form.NorMorning = false;
          form.NorAfternoon = false;
          form.NorNight = false;
          form.SatMorning = false;
          form.SatAfternoon = false;
          form.SatNight = false;
          form.SunMorning = false;
          form.SunAfternoon = false;
          form.SunNight = false;
          lastUpdateTime.value = null;
        }

        pageState.loadSuccess = true;
      } catch (err) {
        console.error(err);
        pageState.loadSuccess = false;
      }
      pageState.loading = true;
    };

    /** 更新資料 */
    const updateData = async () => {
      pageState.saving = true;
      pageState.saveSuccess = null;

      try {
        await UpsertDailyFreeTime(form);
        pageState.saveSuccess = true;
      } catch (err) {
        console.error(err);
        pageState.saveSuccess = false;
      }
      pageState.saving = false;
    };

    const updateForm = async () => {
      await updateData();
      await fetchData();
    };

    const LastUpdateTimeFormat = (date: Date | null) => {
      if (!date) return "-";
      return format(date, "yyyy-MM-dd hh:mm:ss");
    };

    await fetchData();

    return {
      form,
      lastUpdateTime,
      pageState,

      // format
      LastUpdateTimeFormat,

      // data
      updateForm,
    };
  },
});

interface PageState {
  loading: boolean;
  loadSuccess: boolean | null;
  saving: boolean;
  saveSuccess: boolean | null;
}
</script>
